/**
 * Publish the current assembly to the public catalog.
 *
 * Owns its own form state, which is why it is a component rather than a block
 * of JSX inside Header.
 */

import { useState } from "react";
import { Button, Group, Modal, Text, Textarea, TextInput } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../api/client";

interface PublishModalProps {
  opened: boolean;
  onClose: () => void;
  moduleCount: number;
}

export function PublishModal({ opened, onClose, moduleCount }: PublishModalProps) {
  const [publishName, setPublishName] = useState("");
  const [publishDescription, setPublishDescription] = useState("");
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishResult, setPublishResult] = useState<string | null>(null);
  const navigate = useNavigate();

  // Header used to clear the error just before opening; clearing on the way out
  // is the same thing without a frame of stale text when the modal reappears.
  const close = () => {
    setPublishResult(null);
    onClose();
  };

  const handlePublish = async () => {
    if (!window.sessionId) {
      return;
    }
    setPublishBusy(true);
    setPublishResult(null);
    try {
      const result = await apiClient.registerCatalogEntry(
        publishName,
        publishDescription,
        window.sessionId
      );
      if (result.success) {
        close();
        setPublishName("");
        setPublishDescription("");
        navigate(`/catalog?return=${encodeURIComponent(window.sessionId ?? "")}`);
      } else {
        setPublishResult(result.error ?? "Publishing failed");
      }
    } catch (e) {
      setPublishResult(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title="Publish to Catalog"
      centered
      size="md"
    >
      <Text size="sm" c="dimmed" mb="md">
        Publishes the current assembly ({moduleCount} module{moduleCount === 1 ? "" : "s"})
        to the public catalog. Anyone can browse it and open a copy in their own builder.
      </Text>
      <TextInput
        label="Name"
        placeholder="e.g. module_jaxon 4-DOF arm"
        value={publishName}
        onChange={(e) => setPublishName(e.currentTarget.value)}
        required
        mb="sm"
        data-autofocus
      />
      <Textarea
        label="Description"
        placeholder="What is this assembly for? Anything special about how it connects?"
        value={publishDescription}
        onChange={(e) => setPublishDescription(e.currentTarget.value)}
        minRows={3}
        mb="md"
      />
      {publishResult && (
        <Text size="sm" c="red" mb="sm">
          {publishResult}
        </Text>
      )}
      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={close}>
          Cancel
        </Button>
        <Button
          color="teal"
          loading={publishBusy}
          disabled={publishName.trim().length === 0}
          onClick={handlePublish}
        >
          Publish
        </Button>
      </Group>
    </Modal>
  );
}
